import type { IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
import type { ICustomerOptionGroup, ICustomerOptionGroupOption } from "@/data/model/Customer";
import { Format } from "@/data/util/Format";

import { clientLanguage } from "@/shared/localization/language";
import { Ripple } from "@/shared/ui/Ripple";

import { trans } from "@/app/localization/Trans";
import { Helper } from "@/app/ui/Helper";

export class OptionGroup {
    public onChange?: { () };
    private model: ICustomerOptionGroup;
    private $element: JQuery;
    private $options: JQuery;

    private hasQuantity: boolean;
    private isRadio: boolean;
    private showPlusOnPrices = true;

    private subGroups: Array<OptionGroup>;

    constructor(optionGroup: ICustomerOptionGroup, showPlusOnPrices: boolean) {
        this.model = optionGroup;

        this.showPlusOnPrices = showPlusOnPrices;

        this.$element = $('<div class="optiongroup" />').data("ctrl", this);
        if (optionGroup.options.length === 0) this.$element.hide();

        this.$element.append(
            '<div class="name">' + Format.htmlEscape(Format.translate(optionGroup.name, clientLanguage())) + "</div>"
        );

        if (optionGroup.maxOptions !== undefined && optionGroup.maxOptions > optionGroup.options.length)
            optionGroup.maxOptions = optionGroup.options.length;

        const q1 =
            optionGroup.minOptions === undefined || optionGroup.minOptions === 0 ? undefined : optionGroup.minOptions;

        let instructionText = "";
        if (
            q1 === undefined &&
            (optionGroup.maxOptions === undefined ||
                optionGroup.maxOptions === 1 ||
                optionGroup.maxOptions === optionGroup.options.length)
        )
            instructionText += trans.addToCartPopover.optional + ".";
        else if (q1 !== undefined && optionGroup.minOptions === optionGroup.maxOptions)
            instructionText +=
                trans.addToCartPopover.chooseExact
                    .replace("{0}", q1 as any)
                    .replace("{1}", q1 > 1 ? trans.addToCartPopover.optionPlur : trans.addToCartPopover.optionSing) +
                ".";
        else if (optionGroup.maxOptions === undefined && q1 !== undefined)
            instructionText +=
                trans.addToCartPopover.chooseMin
                    .replace("{0}", q1 as any)
                    .replace("{1}", q1 > 1 ? trans.addToCartPopover.optionPlur : trans.addToCartPopover.optionSing) +
                ".";
        else if (optionGroup.maxOptions !== undefined && q1 === undefined)
            instructionText +=
                trans.addToCartPopover.chooseUpTo
                    .replace("{0}", optionGroup.maxOptions as any)
                    .replace(
                        "{1}",
                        optionGroup.maxOptions > 1
                            ? trans.addToCartPopover.optionPlur
                            : trans.addToCartPopover.optionSing
                    ) + ".";
        else if (optionGroup.maxOptions !== undefined && q1 !== undefined)
            instructionText +=
                trans.addToCartPopover.chooseBetween
                    .replace("{0}", q1 as any)
                    .replace("{1}", optionGroup.maxOptions as any) + ".";

        if (
            optionGroup.maxSum !== undefined &&
            optionGroup.maxSum > 1 &&
            optionGroup.options.length > 1 &&
            (optionGroup.maxOptions === undefined || optionGroup.maxOptions > 1) &&
            (optionGroup.maxPerOption === undefined || optionGroup.maxPerOption > 1)
        ) {
            instructionText +=
                " " + trans.addToCartPopover.upToTotalItems.replace("{0}", optionGroup.maxSum as any) + ".";
        }

        if (instructionText.length !== 0) instructionText = instructionText.substring(0, instructionText.length - 1);

        this.$element.append('<div class="instruction">' + instructionText + "</div>");

        this.$options = $('<div class="options" />').appendTo(this.$element);

        this.isRadio = optionGroup.minOptions === 1 && optionGroup.maxOptions === 1;
        this.hasQuantity = optionGroup.maxPerOption !== 1;

        this.subGroups = new Array<OptionGroup>();
        this.renderOptions();
    }

    public getValue(): Array<IArticleWithOptions> {
        const options = new Array<IArticleWithOptions>();

        // get selected options
        this.$options.find("> .option.selected").each((i, e) => {
            const opt: ICustomerOptionGroupOption = $(e).data("model");
            const qty = this.hasQuantity ? $(e).data("qty") : 1;
            const entry: IArticleWithOptions = {
                quantity: qty,
                menuCategoryId: undefined,
                articleId: opt.articleId,
                optionGroupId: this.model.optionGroupId,
                options: [],
            };

            $(e)
                .find("> .subgroups > .optiongroup")
                .each((i, e) => {
                    const ctrl: OptionGroup = $(e).data("ctrl");
                    entry.options.push(...ctrl.getValue());
                });

            options.push(entry);
        });

        return options;
    }

    private renderOptions() {
        for (const i in this.model.options) {
            const opt = this.model.options[i];

            const optionRow = $('<div class="option" />').data("model", opt).appendTo(this.$options);
            const inner = $('<div class="inner" />').appendTo(optionRow);

            Ripple.bindRippleOut(
                inner.click((e) => {
                    const target = $(e.currentTarget).parent();
                    if (target.hasClass("disabled")) {
                        e.stopPropagation();
                        return;
                    }

                    this.onClickOption(target);
                }),
                "> .radiobox, > .checkbox"
            );

            if (this.isRadio) {
                inner.append('<div class="radiobox"></div>');
            } else {
                inner.append('<div class="checkbox"></div>');
            }

            inner.append(
                '<div class="name">' + Format.htmlEscape(Format.translate(opt.name, clientLanguage())) + "</div>"
            );

            if (this.hasQuantity) {
                optionRow.data("qty", 0);
                const qtyInput = $('<div class="quantity" />').appendTo(inner);

                // if minQuantity = maxQuantity, then hide increase/decrease btns; if minQuantity is not provided we assume minQuantity = 1
                if (opt.minQuantity ?? 1 == opt.maxQuantity) {
                    qtyInput.addClass("hidden");
                }

                $('<div class="minus btn"><i class="fa fa-minus"></i></div>')
                    .appendTo(qtyInput)
                    .click((e) => {
                        e.stopPropagation();
                        const tar = $(e.currentTarget);
                        if (tar.hasClass("disabled")) return;

                        this.decreaseOptionQuantity(tar.parent().parent().parent());
                    });

                $('<div class="plus btn"><i class="fa fa-plus"></i></div>')
                    .appendTo(qtyInput)
                    .click((e) => {
                        e.stopPropagation();
                        const tar = $(e.currentTarget);
                        if (tar.hasClass("disabled")) return;

                        this.increaseOptionQuantity(tar.parent().parent().parent());
                    });
            }

            const p = Helper.formatPrice(opt);
            $('<div class="price">' + (p === "" ? "" : (this.showPlusOnPrices ? "+" : "") + p) + "</div>")
                .appendTo(inner)
                .click((e) => {
                    e.preventDefault();
                    e.stopPropagation();
                });

            if (opt.preselected) {
                this.selectOption(optionRow, this.hasQuantity);
            } else {
                this.deselectOption(optionRow, this.hasQuantity);
            }
        }

        this.applyDisableRule();
    }

    private onClickOption($option: JQuery) {
        if (this.isRadio) {
            if ($option.hasClass("selected")) return;

            this.selectOption($option);
        } else {
            if ($option.hasClass("selected")) this.deselectOption($option);
            else this.selectOption($option);
        }

        this.applyDisableRule();

        if (this.onChange !== undefined) this.onChange();
    }

    private selectOption($option: JQuery, updateQty?: boolean) {
        if (updateQty === undefined) updateQty = true;

        const optionModel: ICustomerOptionGroupOption = $option.data("model");
        const initialQuantity = optionModel.minQuantity !== undefined ? optionModel.minQuantity : 1;
        if (this.isRadio) {
            const oldSelected = this.$options.find("> .selected");
            if (!oldSelected.is($option)) {
                this.deselectOption(oldSelected);
                $option.addClass("selected");

                if (updateQty && this.hasQuantity) this.setOptionQuantity($option, initialQuantity);

                const sub: ICustomerOptionGroupOption = $option.data("model");
                if (sub.optionGroups.length !== 0)
                    this.addSubGroupsToOption($option, sub.optionGroups, sub.price !== undefined && sub.price !== 0);
            }
        } else {
            if (!$option.hasClass("selected")) {
                $option.addClass("selected");

                if (updateQty && this.hasQuantity) this.setOptionQuantity($option, initialQuantity);

                const sub: ICustomerOptionGroupOption = $option.data("model");
                if (sub.optionGroups.length !== 0)
                    this.addSubGroupsToOption($option, sub.optionGroups, sub.price !== undefined && sub.price !== 0);
            }
        }
    }

    private deselectOption($option: JQuery, updateQty?: boolean) {
        if (updateQty === undefined) updateQty = true;

        if ($option.hasClass("selected")) {
            $option.removeClass("selected");

            if (updateQty && this.hasQuantity) this.setOptionQuantity($option, 0);

            const sub: ICustomerOptionGroupOption = $option.data("model");
            if (sub.optionGroups.length !== 0) this.removeSubGroupsFromOption($option);
        }
    }

    private decreaseOptionQuantity($option: JQuery) {
        if (!this.hasQuantity) return;

        const currentQty: number = $option.data("qty");
        const newQty = currentQty - 1;
        if (newQty < 0) return;

        this.setOptionQuantity($option, newQty);
        if (newQty === 0) this.deselectOption($option, false);

        this.applyDisableRule();

        if (this.onChange !== undefined) this.onChange();
    }

    private increaseOptionQuantity($option: JQuery) {
        if (!this.hasQuantity) return;

        const optionModel: ICustomerOptionGroupOption = $option.data("model");
        const currentQty: number = $option.data("qty");
        let newQty = currentQty + 1;
        if (optionModel.minQuantity !== undefined && newQty < optionModel.minQuantity) newQty = optionModel.minQuantity;

        if (this.model.maxPerOption !== undefined && newQty > this.model.maxPerOption) return;

        this.setOptionQuantity($option, newQty);
        if (currentQty === 0) this.selectOption($option, false);

        this.applyDisableRule();

        if (this.onChange !== undefined) this.onChange();
    }

    private setOptionQuantity($option: JQuery, qty: number) {
        if (!this.hasQuantity) return;

        $option.data("qty", qty);

        const optionModel: ICustomerOptionGroupOption = $option.data("model");
        if (optionModel.maxQuantity == 1) return;

        const quantityContainer = $option.find("> .inner > .name > .qty");
        if (qty === 0) {
            quantityContainer.remove();
            return;
        }

        if (quantityContainer.length === 0)
            $option.find("> .inner > .name").prepend('<div class="qty">' + qty + "x</div>");
        else quantityContainer.html(qty + "x");
    }

    private applyDisableRule() {
        const selectedOptions = this.$options.find("> .selected");
        let totalQuantity = 0;

        if (!this.hasQuantity) totalQuantity += selectedOptions.length;
        else {
            selectedOptions.each((i, e) => {
                totalQuantity += $(e).data("qty");
            });
        }

        this.$options.find("> .option").each((i, e) => {
            const opt = $(e);

            const optIsSelected = opt.hasClass("selected");

            let allowNewOption = true;
            if (!this.isRadio) {
                if (this.model.maxSum !== undefined && this.model.maxSum <= totalQuantity) allowNewOption = false;

                if (this.model.maxOptions !== undefined && this.model.maxOptions <= selectedOptions.length)
                    allowNewOption = false;

                if (allowNewOption) opt.removeClass("disabled");
                else if (!optIsSelected) opt.addClass("disabled");
            }

            if (this.hasQuantity) {
                const qty: number = opt.data("qty");
                const optionModel: ICustomerOptionGroupOption = opt.data("model");
                const incr = opt.find("> .inner > .quantity > .plus");

                let allowIncrease = true;
                if (this.model.maxSum !== undefined && this.model.maxSum <= totalQuantity) allowIncrease = false;

                if (this.model.maxPerOption !== undefined && this.model.maxPerOption <= qty) allowIncrease = false;

                if (
                    this.model.maxPerOption === undefined &&
                    optionModel.maxQuantity !== undefined &&
                    qty >= optionModel.maxQuantity
                )
                    allowIncrease = false;

                if (allowIncrease && !allowNewOption && !optIsSelected) allowIncrease = false;

                if (allowIncrease) incr.removeClass("disabled");
                else incr.addClass("disabled");

                let allowDecrease = true;
                if (!optIsSelected) allowDecrease = false;

                const minQuantity = optionModel.minQuantity ?? 1; // if no quantity is provided, min quantity = 1
                if (allowDecrease && qty <= minQuantity) allowDecrease = false;

                const decr = opt.find("> .inner > .quantity > .minus");
                if (allowDecrease) decr.removeClass("disabled");
                else decr.addClass("disabled");
            }
        });
    }

    private addSubGroupsToOption($option: JQuery, subGroups: Array<ICustomerOptionGroup>, showPlusOnPrices: boolean) {
        let subContainer = $option.find("> .subgroups");
        if (subContainer.length === 0) subContainer = $('<div class="subgroups" />').appendTo($option);

        for (const i in subGroups) {
            const g = new OptionGroup(subGroups[i], showPlusOnPrices).appendTo(subContainer);
            this.subGroups.push(g);
            g.onChange = () => {
                if (this.onChange !== undefined) this.onChange();
            };
        }
    }

    private removeSubGroupsFromOption($option: JQuery) {
        const sg = $option.find("> .subgroups");
        if (sg.length === 0) return;

        $option.find("> .subgroups > .optiongroup").each((i, e) => {
            const ctrl: OptionGroup = $(e).data("ctrl");
            const idx = this.subGroups.indexOf(ctrl);
            if (idx !== -1) this.subGroups.splice(idx, 1);

            ctrl.destroy();
        });

        sg.remove();
    }

    public isValid(): boolean {
        if (this.model.minOptions === undefined && this.model.maxOptions === undefined && this.subGroups.length === 0)
            return true;

        const selected = this.$options.find("> .option.selected").length;
        if (this.model.minOptions !== undefined && this.model.minOptions > selected) return false;

        if (this.model.maxOptions !== undefined && this.model.maxOptions < selected) return false;

        for (const i in this.subGroups) {
            if (!this.subGroups[i].isValid()) return false;
        }

        return true;
    }

    public calculatePrice(): number {
        let price = 0;

        this.$options.find("> .option.selected").each((i, e) => {
            let qty = 1;
            if (this.hasQuantity) qty = $(e).data("qty");

            const optPrice = Helper.sitePrice($(e).data("model"));
            if (optPrice !== undefined && optPrice !== 0) price += qty * optPrice;

            $(e)
                .find("> .subgroups > .optiongroup")
                .each((i, a) => {
                    const ctrl: OptionGroup = $(a).data("ctrl");
                    price += ctrl.calculatePrice() * qty;
                });
        });

        return price;
    }

    public destroy() {
        this.$element.remove();
    }

    public appendTo($element: JQuery): OptionGroup {
        this.$element.appendTo($element);
        return this;
    }
}
